import type { UsageClient } from "./usage-client";

const IDLE_MS = 5 * 60_000;
const CHECKPOINT_MS = 60_000;

export class ActivitySession {
  private isVisible = false;
  private segmentStart: number | null = null;
  private lastActivity: number | null = null;

  constructor(private readonly emit: (kind: "foreground_interval", seconds: number) => void) {}

  visible(at: number): void {
    if (this.isVisible) return;
    this.isVisible = true;
    this.segmentStart = at;
    this.lastActivity = at;
  }

  activity(at: number): void {
    if (!this.isVisible) return;
    if (
      this.segmentStart !== null
      && this.lastActivity !== null
      && at >= this.lastActivity + IDLE_MS
    ) {
      this.emitBounded(this.segmentStart, this.lastActivity + IDLE_MS);
      this.segmentStart = at;
    } else if (this.segmentStart === null) {
      this.segmentStart = at;
    }
    this.lastActivity = at;
  }

  tick(at: number): void {
    if (!this.isVisible || this.segmentStart === null || this.lastActivity === null) return;
    const idleBoundary = this.lastActivity + IDLE_MS;
    const cutoff = Math.min(at, idleBoundary);
    if (cutoff - this.segmentStart >= CHECKPOINT_MS || at >= idleBoundary) {
      this.emitBounded(this.segmentStart, cutoff);
      this.segmentStart = cutoff;
    }
    if (at >= idleBoundary) this.segmentStart = null;
  }

  hidden(at: number): void {
    if (!this.isVisible) return;
    if (this.segmentStart !== null && this.lastActivity !== null) {
      this.emitBounded(this.segmentStart, Math.min(at, this.lastActivity + IDLE_MS));
    }
    this.isVisible = false;
    this.segmentStart = null;
    this.lastActivity = null;
  }

  private emitBounded(from: number, to: number): void {
    let seconds = Math.max(0, Math.floor((to - from) / 1000));
    while (seconds > 0) {
      const interval = Math.min(300, seconds);
      this.emit("foreground_interval", interval);
      seconds -= interval;
    }
  }
}

export function startActivityTracking(
  client: Pick<UsageClient, "track" | "flush">,
  projectRef: () => string | null,
  clock: () => number = Date.now,
): () => void {
  const session = new ActivitySession((kind, seconds) => {
    void client.track(kind, projectRef(), seconds);
  });
  const now = clock;
  const foreground = () => {
    if (document.visibilityState === "visible") {
      session.visible(now());
      void client.track("app_foreground", projectRef());
      void client.flush();
    } else {
      session.hidden(now());
    }
  };
  const activity = () => session.activity(now());
  const pagehide = () => {
    session.hidden(now());
    void client.flush();
  };
  const online = () => void client.flush();
  const usageQueued = () => void client.flush();

  foreground();
  document.addEventListener("visibilitychange", foreground);
  for (const type of ["pointerdown", "keydown", "touchstart"] as const) {
    window.addEventListener(type, activity, { passive: true });
  }
  window.addEventListener("pagehide", pagehide);
  window.addEventListener("online", online);
  window.addEventListener("mark-usage-queued", usageQueued);
  const timer = window.setInterval(() => session.tick(now()), CHECKPOINT_MS);
  return () => {
    pagehide();
    document.removeEventListener("visibilitychange", foreground);
    for (const type of ["pointerdown", "keydown", "touchstart"] as const) {
      window.removeEventListener(type, activity);
    }
    window.removeEventListener("pagehide", pagehide);
    window.removeEventListener("online", online);
    window.removeEventListener("mark-usage-queued", usageQueued);
    window.clearInterval(timer);
  };
}
